import { api, contentApi, idStr } from "../email-campaigns/api";
import { authHeaders, apiRoot } from "../reports/common";
import ServiceFactory from "../../service";

/*
 * Compose-editor backend contracts (2026-10-04). Everything here fails soft:
 * `isMissing(e)` is true for 404/405/501 so a caller hides the feature until
 * the mail service ships the route; any other error surfaces a friendly line.
 *
 *  POST /v1/campaign/email/render {design, firstName?, personId?}     → {html, …}
 *  GET  /v1/campaign/email/compose/products?q=&$top=20              → {items:[{id,title,imageUrl,price,url}]}
 *  GET  /v1/content/assets?kind=image&q=&$top=                        → {items:[ContentAssetModel]}
 *  POST /v1/content/assets/upload-url + PUT + POST /v1/content/assets  (Content Hub upload)
 *  POST /v1/campaign/email/compose/images (multipart "file")          → {url,id}   (fallback)
 *  GET/POST/PUT/DELETE /v1/campaign/email/blocks                      saved blocks
 *  POST /v1/campaign/email/compose/suggest                            → draft copy; 503 {error:"aiUnavailable"}
 */

export function isMissing(e) {
  const s = e && e.status;
  return s === 404 || s === 405 || s === 501;
}
export function friendly(e, fallback) {
  if (!e) return fallback;
  if (typeof e.message === "string" && e.message && e.message.length < 200 && !/^HTTP \d+$/.test(e.message)) return e.message;
  return fallback;
}
function qs(obj) {
  const parts = [];
  Object.keys(obj || {}).forEach(k => {
    const v = obj[k];
    if (v === undefined || v === null || v === "") return;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  });
  return parts.length ? "?" + parts.join("&") : "";
}

// ---- render -----------------------------------------------------------------

export function renderDesign(design, opts) {
  const body = {
    design,
    firstName: "Dana"
  };
  if (opts && opts.personId) {
    body.personId = opts.personId;
    delete body.firstName;
  }
  return api("POST", "/render", body);
}

// ---- products ---------------------------------------------------------------

export async function searchProducts(q) {
  const d = await api("GET", `/compose/products${qs({
    q,
    $top: 20
  })}`);
  return (d && d.items || []).map(p => ({
    id: idStr(p.id),
    title: p.title || "",
    imageUrl: p.imageUrl || "",
    price: p.price,
    url: p.url || ""
  }));
}

// ---- images -----------------------------------------------------------------

/** Recent image assets from the Content Hub library. */
export async function listImageAssets(q) {
  const d = await contentApi("GET", `/assets${qs({
    kind: "image",
    q,
    $top: 48
  })}`);
  return (d && d.items || []).map(a => ({
    id: idStr(a.id),
    url: a.latestUrl || a.url,
    title: a.title || "",
    thumb: a.posterUrl || a.latestUrl || a.url,
    createdDate: a.createdDate
  }));
}

/**
 * Upload one image. The Content Hub route first (presigned PUT, then the asset
 * is registered so it shows in the library); on any 4xx there, the composer's
 * own multipart route.
 */
export async function uploadImage(file) {
  let first = null;
  try {
    const pre = await contentApi("POST", "/assets/upload-url", {
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      bytes: file.size
    });
    if (!pre || !pre.uploadUrl) throw Object.assign(new Error("Could not prepare the upload."), {
      status: 400
    });
    const put = await fetch(pre.uploadUrl, {
      method: "PUT",
      headers: {
        "content-type": pre.contentType || file.type || "application/octet-stream"
      },
      body: file
    });
    if (!put.ok) throw Object.assign(new Error(`Upload failed (${put.status}).`), {
      status: put.status
    });
    const a = await contentApi("POST", "/assets", {
      kind: pre.kind || "image",
      url: pre.url,
      title: (file.name || "image").replace(/\.[^.]+$/, ""),
      source: "upload"
    });
    return {
      url: pre.url,
      id: a && idStr(a.id || a.Id)
    };
  } catch (e) {
    first = e;
    if (!(e && e.status >= 400 && e.status < 500)) throw e;
  }
  const fd = new FormData();
  fd.append("file", file, file.name);
  const res = await fetch(`${apiRoot()}/campaign/email/compose/images`, {
    method: "POST",
    headers: authHeaders(),
    body: fd
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(d && (d.message || d.error) || first && first.message || `Upload failed (${res.status}).`);
    err.status = res.status;
    throw err;
  }
  return {
    url: d.url,
    id: idStr(d.id)
  };
}

// ---- saved blocks -----------------------------------------------------------

export async function listBlocks() {
  const d = await api("GET", "/blocks");
  return (d && d.items || []).map(normBlock);
}
export async function createBlock(name, section) {
  return normBlock(await api("POST", "/blocks", {
    name,
    section
  }));
}
export async function renameBlock(id, name) {
  return normBlock(await api("PUT", `/blocks/${id}`, {
    name
  }));
}
export async function deleteBlock(id) {
  return api("DELETE", `/blocks/${id}`);
}
function normBlock(b) {
  if (!b) return null;
  let section = b.section;
  if (typeof section === "string") {
    try {
      section = JSON.parse(section);
    } catch (e) {
      section = null;
    }
  }
  return {
    id: idStr(b.id),
    name: b.name || "",
    section: section || null,
    createdBy: b.createdBy || "",
    createdDate: b.createdDate || null
  };
}

// ---- AI drafting ------------------------------------------------------------

// One flag for the whole App session: the first 503 {error:"aiUnavailable"}
// hides every "Draft with AI" button; subscribers re-render.
const ai = {
  unavailable: false,
  hidden: false,
  listeners: new Set()
};
export function aiAvailable() {
  return !ai.hidden;
}
export function onAiChange(fn) {
  ai.listeners.add(fn);
  return () => ai.listeners.delete(fn);
}
function setAiHidden() {
  if (ai.hidden) return;
  ai.hidden = true;
  ai.listeners.forEach(fn => {
    try {
      fn();
    } catch (e) {/* noop */}
  });
}
export const AI_UNAVAILABLE_TEXT = "AI drafting isn't enabled for this workspace yet.";

/** Returns the suggestion, or throws; `e.aiUnavailable` is set (and the buttons hide) on the 503. */
export async function suggest(body) {
  try {
    return await api("POST", "/compose/suggest", body);
  } catch (e) {
    const code = e && (e.code || e.data && e.data.error);
    if (e && (e.status === 503 || code === "aiUnavailable" || isMissing(e))) {
      e.aiUnavailable = true;
      setAiHidden();
    }
    throw e;
  }
}

// ---- contacts (preview as a real person) --------------------------------------

/** Name/email search over the contacts roster (the federated search's endpoint). */
export async function searchContacts(term) {
  const t = String(term || "").trim().toLowerCase();
  if (!t) return [];
  const where = "Name.ToLower().Contains(@0)||Email.ToLower().Contains(@0)";
  const Service = ServiceFactory.getType(`/contacts/metrics?$where=${encodeURIComponent(where)}&$params=${encodeURIComponent(t)}&$top=8`, "activity");
  const d = await new Service().getAsync();
  const rows = Array.isArray(d) ? d : d && (d.items || d.data) || [];
  return rows.map(r => ({
    id: idStr(r.id || r.Id || r.personId || r.PersonId),
    name: r.name || r.Name || "",
    email: r.email || r.Email || ""
  })).filter(r => r.id);
}